// src/components/ManifestoCard.tsx
import React, { useState } from 'react';
import { 
  Award, 
  Calendar, 
  User, 
  MapPin, 
  Eye, 
  Share2,
  Download,
  MessageSquare,
  ChevronDown,
  ChevronUp,
  Target,
  TrendingUp,
  FileText
} from 'lucide-react';
import toast from 'react-hot-toast';

export interface Manifesto {
  id: number;
  aspirantId: number;
  aspirantName: string;
  aspirantPosition: string;
  wardName: string;
  constituencyName: string;
  title: string;
  content: string;
  summary: string;
  visionStatement: string;
  missionStatement: string;
  source: string;
  sourceDescription: string;
  status: string;
  statusDisplay: string;
  topIssueCategories: string[];
  sourceReportIds: string[];
  totalCitizenReportsAnalyzed: number;
  documentUrl?: string;
  publishedAt?: string;
  createdAt: string;
  updatedAt?: string;
  version: number;
  isPublic: boolean;
  feedbackLink?: string;
  
  // Analysis data
  totalReportsInWard?: number;
  topIssueCategory?: string;
  totalReportsForTopIssue?: number;
  percentageOfTotalReports?: number;
}

interface ManifestoCardProps {
  manifesto: Manifesto;
  onShare?: (manifesto: Manifesto) => void;
  onDownload?: (url: string, filename: string) => void;
  onFeedback?: (feedbackLink: string) => void;
}

const ManifestoCard: React.FC<ManifestoCardProps> = ({
  manifesto,
  onShare,
  onDownload,
  onFeedback
}) => {
  const [expanded, setExpanded] = useState(false);

  const handleShare = () => {
    if (onShare) {
      onShare(manifesto);
    } else {
      const shareUrl = `${window.location.origin}/citizen/manifesto/${manifesto.aspirantId}/${manifesto.id}`;
      const shareText = `Check out ${manifesto.aspirantName}'s manifesto: ${manifesto.title}`;
      
      if (navigator.share) {
        navigator.share({
          title: manifesto.title,
          text: shareText,
          url: shareUrl,
        }).catch(() => {
          navigator.clipboard.writeText(shareUrl);
          toast.success('Link copied to clipboard!');
        });
      } else {
        navigator.clipboard.writeText(shareUrl);
        toast.success('Link copied to clipboard!');
      }
    }
  };

  const handleDownload = () => {
    if (manifesto.documentUrl && onDownload) {
      onDownload(manifesto.documentUrl, `${manifesto.aspirantName}-manifesto-v${manifesto.version}.pdf`);
    } else if (manifesto.documentUrl) {
      window.open(manifesto.documentUrl, '_blank');
    } else {
      // Create a text file with the manifesto content
      const content = `
${manifesto.title}
${'='.repeat(manifesto.title.length)}

Aspirant: ${manifesto.aspirantName}
Position: ${manifesto.aspirantPosition}
Ward: ${manifesto.wardName}
Published: ${new Date(manifesto.publishedAt || manifesto.createdAt).toLocaleDateString()}
Version: ${manifesto.version}

SUMMARY
${'-'.repeat(20)}
${manifesto.summary}

VISION STATEMENT
${'-'.repeat(20)}
${manifesto.visionStatement || 'Not provided'}

MISSION STATEMENT
${'-'.repeat(20)}
${manifesto.missionStatement || 'Not provided'}

CONTENT
${'-'.repeat(20)}
${manifesto.content}

${manifesto.topIssueCategories?.length ? `
TOP ISSUES ADDRESSED
${'-'.repeat(20)}
${manifesto.topIssueCategories.join('\n')}
` : ''}

${manifesto.totalCitizenReportsAnalyzed ? `
Based on ${manifesto.totalCitizenReportsAnalyzed} citizen reports analyzed.
` : ''}
      `;
      
      const blob = new Blob([content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${manifesto.aspirantName}-manifesto-v${manifesto.version}.txt`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Manifesto downloaded!');
    }
  };

  const handleFeedback = () => {
    if (manifesto.feedbackLink && onFeedback) {
      onFeedback(manifesto.feedbackLink);
    } else if (manifesto.feedbackLink) {
      window.open(manifesto.feedbackLink, '_blank');
    } else {
      toast.error('No feedback link available for this manifesto');
    }
  };

  const getSourceBadgeColor = (source: string) => {
    switch(source) {
      case 'CITIZEN_REPORTS': return 'bg-green-100 text-green-800';
      case 'CUSTOM': return 'bg-blue-100 text-blue-800';
      case 'TEMPLATE': return 'bg-purple-100 text-purple-800';
      case 'HYBRID': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg transition-all">
      {/* Header */}
      <div className="p-6 border-b border-gray-100">
        <div className="flex items-start justify-between mb-3">
          <div>
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getSourceBadgeColor(manifesto.source)} mb-2`}>
              {manifesto.sourceDescription || manifesto.source}
            </span>
            <h3 className="text-xl font-bold text-gray-900">{manifesto.title}</h3>
            <p className="text-sm text-gray-600 mt-1">Version {manifesto.version}</p>
          </div>
          {manifesto.isPublic && (
            <span className="inline-flex items-center px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
              <Eye size={12} className="mr-1" />
              Public
            </span>
          )}
        </div>

        {/* Aspirant Info */}
        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
          <div className="flex items-center">
            <User size={14} className="mr-1 text-gray-400" />
            <span className="font-medium">{manifesto.aspirantName}</span>
          </div>
          <div className="flex items-center">
            <MapPin size={14} className="mr-1 text-gray-400" />
            <span>{manifesto.wardName}</span>
          </div>
          <div className="flex items-center">
            <Calendar size={14} className="mr-1 text-gray-400" />
            <span>{new Date(manifesto.publishedAt || manifesto.createdAt).toLocaleDateString()}</span>
          </div>
        </div>

        {/* Quick Stats */}
        {manifesto.totalCitizenReportsAnalyzed && manifesto.totalCitizenReportsAnalyzed > 0 && (
          <div className="mt-4 flex flex-wrap items-center gap-4 p-3 bg-blue-50 rounded-lg">
            <div className="flex items-center">
              <TrendingUp size={16} className="text-blue-600 mr-2" />
              <span className="text-sm">
                <span className="font-bold text-blue-700">{manifesto.totalCitizenReportsAnalyzed}</span>
                <span className="text-blue-600"> citizen reports analyzed</span>
              </span>
            </div>
            {manifesto.topIssueCategory && (
              <div className="flex items-center">
                <Target size={16} className="text-blue-600 mr-2" />
                <span className="text-sm text-blue-600">
                  Top issue: <span className="font-medium">{manifesto.topIssueCategory}</span>
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Summary */}
      <div className="px-6 py-4">
        <p className="text-gray-700">{manifesto.summary}</p>
      </div>

      {/* Vision & Mission (Collapsible) */}
      {(manifesto.visionStatement || manifesto.missionStatement) && (
        <div className="px-6 pb-2">
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            {expanded ? (
              <>
                <ChevronUp size={16} className="mr-1" />
                Hide Vision & Mission
              </>
            ) : (
              <>
                <ChevronDown size={16} className="mr-1" />
                Show Vision & Mission
              </>
            )}
          </button>
          
          {expanded && (
            <div className="mt-4 space-y-4">
              {manifesto.visionStatement && (
                <div className="p-4 bg-purple-50 rounded-lg">
                  <h4 className="font-medium text-purple-900 mb-2 flex items-center">
                    <Award size={16} className="mr-2" />
                    Vision Statement
                  </h4>
                  <p className="text-purple-800 text-sm">{manifesto.visionStatement}</p>
                </div>
              )}
              
              {manifesto.missionStatement && (
                <div className="p-4 bg-indigo-50 rounded-lg">
                  <h4 className="font-medium text-indigo-900 mb-2 flex items-center">
                    <Target size={16} className="mr-2" />
                    Mission Statement
                  </h4>
                  <p className="text-indigo-800 text-sm">{manifesto.missionStatement}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Top Issues */}
      {manifesto.topIssueCategories && manifesto.topIssueCategories.length > 0 && (
        <div className="px-6 py-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Key Issues Addressed:</h4>
          <div className="flex flex-wrap gap-2">
            {manifesto.topIssueCategories.map((issue, index) => (
              <span
                key={index}
                className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs"
              >
                {issue}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex flex-wrap gap-2">
        <button
          onClick={handleShare}
          className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-white rounded-lg transition-colors"
        >
          <Share2 size={14} />
          Share
        </button>
        
        <button
          onClick={handleDownload}
          className="flex items-center gap-1 px-3 py-2 text-sm text-blue-600 hover:text-blue-700 hover:bg-white rounded-lg transition-colors"
        >
          <Download size={14} />
          Download
        </button>
        
        {manifesto.feedbackLink && (
          <button
            onClick={handleFeedback}
            className="flex items-center gap-1 px-3 py-2 text-sm text-green-600 hover:text-green-700 hover:bg-white rounded-lg transition-colors"
          >
            <MessageSquare size={14} />
            Give Feedback
          </button>
        )}
        
        <button
          onClick={() => window.open(`/citizen/aspirant/${manifesto.aspirantId}`, '_blank')}
          className="flex items-center gap-1 px-3 py-2 text-sm text-purple-600 hover:text-purple-700 hover:bg-white rounded-lg transition-colors ml-auto"
        >
          <User size={14} />
          View Aspirant
        </button>
      </div>
    </div>
  );
};

export default ManifestoCard;